import backgroundTextureSeafoam from 'assets/images/vxl/component/productIntroScreen/background-texture-seafoam.png';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import {
  ErrorModel,
  type PayOverTimeErrorConfig,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import useSubmitPayOverTimeOnboardingStep, {
  PayOverTimeOnboardingStatus,
} from 'features/payOverTime/hooks/useSubmitPayOverTimeOnboardingStep';
import {
  setPayOverTimePaymentPlanStateAtom,
  transactionIdAtom,
} from 'features/payOverTime/store/payOverTimeOnboardingStore';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import usePaymentPlanAccount from 'shared/hooks/usePaymentPlanAccount/usePaymentPlanAccount';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import theme from 'shared/styles/theme';

import ApplyNowContent from './ApplyNowContent';

enum ApplyNowFlowError {
  NoAccount = 'NoAccount',
  MissingTransaction = 'MissingTransaction',
}

const ApplyNowLandingPage = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'applyNow',
  });

  const { from, transactionId } = useLocalSearchParams<{
    from?: string;
    transactionId?: string;
  }>();

  const isLandingPageRoute = pathname === '/pay-over-time/landing-page';

  useViewDidFocusScreenAnalyticsEvent({
    key: isLandingPageRoute
      ? '/pay-over-time/landing-page'
      : '/pay-over-time/apply-now',
  });

  const setTransactionId = useSetAtom(transactionIdAtom);
  const setPayOverTimePaymentPlanState = useSetAtom(
    setPayOverTimePaymentPlanStateAtom,
  );
  const [flowError, setFlowError] = useState<ApplyNowFlowError | null>(null);
  const { submitOnboardingStep, fetching: isSubmitting } =
    useSubmitPayOverTimeOnboardingStep();
  const [errorModel, setErrorModel] = useState<ErrorModel | null>(null);
  const { account: checkingAccount, fetching: isAccountLoading } =
    usePaymentPlanAccount();
  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    if (from === 'discover-more') {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  useBackHandler(goHome);

  const dismissFlowError = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  const handleDiscoveryFlowPress = () => {
    setFlowError(null);

    if (!checkingAccount?.accountId) {
      setFlowError(ApplyNowFlowError.NoAccount);
      return;
    }

    router.navigate({
      pathname: '/accounts/[id]',
      params: { id: checkingAccount.accountId },
    });
  };

  const handleTransactionFlowPress = async () => {
    setFlowError(null);
    setErrorModel(null);

    if (!transactionId) {
      setFlowError(ApplyNowFlowError.MissingTransaction);
      return;
    }

    setTransactionId(transactionId);

    const outcome = await submitOnboardingStep({ transactionId });

    switch (outcome.status) {
      case PayOverTimeOnboardingStatus.PaymentPlan:
        setPayOverTimePaymentPlanState(outcome.paymentPlan);
        router.navigate('/pay-over-time/payment-plan');
        break;
      case PayOverTimeOnboardingStatus.Declined:
        router.navigate('/pay-over-time/application-decline');
        break;
      case PayOverTimeOnboardingStatus.Error:
        setErrorModel(outcome.errorModel);
        break;
      case PayOverTimeOnboardingStatus.Cancelled:
        break;
    }
  };

  const handlePrimaryPress = () => {
    if (isLandingPageRoute) {
      handleDiscoveryFlowPress();
      return;
    }

    void handleTransactionFlowPress();
  };

  const resolveErrorConfig = (): PayOverTimeErrorConfig | null => {
    if (errorModel) {
      return {
        screenType: errorModel.screenType,
        messageHeadline: errorModel.messageHeadline,
        messageBody: errorModel.messageBody,
        primaryButton: {
          title: translatedText('done'),
          onPress: () => setErrorModel(null),
        },
      };
    }

    if (flowError) {
      const keyPrefix =
        flowError === ApplyNowFlowError.NoAccount
          ? 'noAccountError'
          : 'missingTransactionError';

      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: translatedText(`${keyPrefix}.title`),
        messageBody: translatedText(`${keyPrefix}.body`),
        primaryButton: {
          title: translatedText('done'),
          onPress: dismissFlowError,
        },
      };
    }

    return null;
  };

  const errorConfig = resolveErrorConfig();

  const isCtaLoading = isLandingPageRoute ? isAccountLoading : isSubmitting;

  if (errorConfig) {
    return (
      <View style={styles.screen}>
        <Stack.Screen
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
        <ErrorScreen
          screenType={errorConfig.screenType}
          messageHeadline={errorConfig.messageHeadline}
          messageBody={errorConfig.messageBody}
          primaryButton={errorConfig.primaryButton}
        />
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          gestureEnabled: false,
          title: translatedText('navigationTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} transparent primaryOnPress={goHome} />
          ),
        }}
      />
      <ProductIntroScreen
        headlineText={translatedText('screenTitle')}
        headlineTextStyle="expressive"
        backgroundTexture={backgroundTextureSeafoam}
        primaryButton={{
          label: isLandingPageRoute
            ? translatedText('landingPageButton')
            : translatedText('button'),
          analyticsId: 'applyNowClicked',
          onPress: handlePrimaryPress,
          isLoading: isCtaLoading,
          isDisabled: isCtaLoading,
        }}
        bodyContent={<ApplyNowContent />}
        visual="SHOPPING_A"
        topPadding={80}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default ApplyNowLandingPage;
